import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { ILabels } from "libraries/interfaces/ILabels";
import { ISortOutput, SortDirection } from "libraries/interfaces/ISort";
import { Yii } from "libraries/interfaces/Yii";
import { IAirports, ICarrier, IFareFamilyDictionary, IQuotes } from "libraries/service/resources/airTrip/IGetAirTripQuotes";
import { IOnAlternativeSelectOutput } from "../fare-family-benchmark/fare-family-benchmark.component";
import {ISegmentSelect} from "../../../../shared/segment-list/all-segment-list.component";
import {
    EQuoteSorterActions, QuoteSorterService
} from "app_locobook/pages/quote-page/components/quote-list-container/quote-sorter/quote-sorter.service";

export interface IOutSelectQuote {
    quoteId: string;
    selectedJourneys: string[];
}

@Component({
    selector: 'quote-list-container',
    template: `
        <card>
            <div class="card-header-content">
                <div class="title">
                    <h1>{{ wording.quotes }}</h1>
                    <h2 *ngIf="quotes && selectedQuoteId && quotes[selectedQuoteId] && quotes[selectedQuoteId].title"
                        class="quotes-title">{{ quotes[selectedQuoteId].title }}</h2>
                </div>

                <div class="btn-container">
                    <ng-container *ngIf="this.quotes">
                        <quote-sorter *permissionIf="'iqba.quote.result.sort'"
                                      [sortStorageKey]="sortStorageKey"
                                      [sortDirection]="sortDirection"
                                      [selectedActionName]="selectedActionName"
                                      (onSort)="sort($event)"></quote-sorter>
                        <btn *permissionIf="selectedOfferShowPermission"
                             [label]="wording.selectedQuote"
                             (onClick)="viewSelectedQuote()" type="actionMore"></btn>
                    </ng-container>

                </div>
            </div>
            <div class="card-body-content" *ngIf="quotes">
                <quote-result
                        *ngFor="let quoteId of (quotes | keys); trackBy: trackByFn; let isLast=last; let quoteIndex=index;"
                        class="quote-result e2e-quote-{{quoteIndex}}"
                        [isSelectedQuote]="quoteId === selectedQuoteId"
                        [tripId]="tripId"
                        [quoteId]="quoteId"
                        [price]="price"
                        [quote]="quotes[quoteId]"
                        [originalRequest]="originalRequest"
                        [airports]="airports"
                        [fareFamilyDictionary]="fareFamilyDictionary"
                        [carriers]="carriers"
                        [isLast]="isLast"
                        [isOfferSelection]="isOfferSelection"
                        [isModifySegment]="isModifySegment"
                        [vccPermission]="vccPermission"
                        [ndcPermission]="ndcPermission"
                        [higherPriceWidth]="higherPriceWidth"
                        [mustEmitPriceWidth]="true"
                        [mustRedirectAfterSelection]="mustRedirectAfterSelection"
                        [sessionPostOnUpdateBooking]="sessionPostOnUpdateBooking"
                        (onViewSelectedQuote)="onViewSelectedQuote.emit($event)"
                        (onSelectedQuote)="selectQuote($event)"
                        (onChooseJourney)="setJourneySelection($event)"
                        (onIsReady)="sort(sortOutput)"
                        (onSelectedQuoteOnly)="selectQuoteOnly($event)"
                        (onAlternativeSelect)="alternativeSelect($event)"
                        (onSelectOtherFlight)="onSelectOtherFlight.emit($event)"></quote-result>
            </div>
            <div class="card-body-content">
                <ng-content select=".notification-wrapper"></ng-content>
            </div>
        </card>`,
    styles: [`
        .quotes-title {
            font-size: 1.5rem;
        }
        .btn-container{
            white-space: nowrap;
        }


    `]
})
export class QuoteListContainerComponent implements OnChanges {

    public readonly wording: ILabels = {
        quotes: Yii.t('wui-iqba-js', 'Toutes les propositions'),
        selectedQuote: Yii.t('wui-iqba-js', 'Afficher la proposition sélectionnée'),
    };

    @Output()
    public readonly onViewSelectedQuote: EventEmitter<string> = new EventEmitter();
    @Output()
    public readonly onSelectedQuote: EventEmitter<IOutSelectQuote> = new EventEmitter();
    @Output()
    public readonly onAlternativeSelect: EventEmitter<IOnAlternativeSelectOutput> = new EventEmitter<IOnAlternativeSelectOutput>();
    @Output()
    public readonly onSelectedQuoteOnly: EventEmitter<IOutSelectQuote> = new EventEmitter<IOutSelectQuote>();
    @Output()
    public readonly onSelectOtherFlight: EventEmitter<IOutSelectQuote> = new EventEmitter<IOutSelectQuote>();

    @Input()
    public mustRedirectAfterSelection: boolean = true;
    @Input()
    public quotes: IQuotes;
    @Input()
    public sessionPostOnUpdateBooking: any = null;
    @Input()
    public tripId: string;
    @Input()
    public selectedQuoteId: string;
    @Input()
    public originalRequest: any;
    @Input()
    public airports: IAirports;
    @Input()
    public carriers: ICarrier;
    @Input()
    public fareFamilyDictionary: { [key: string]: IFareFamilyDictionary };
    @Input()
    public price: any;
    @Input()
    public isOfferSelection: boolean;
    @Input()
    public isModifySegment: boolean;
    @Input()
    public vccPermission: boolean;
    @Input()
    public ndcPermission: boolean;


    public sortDirection: SortDirection = SortDirection.descending;
    public selectedActionName: string = EQuoteSorterActions.price;
    public sortStorageKey: string = 'quoteList';
    public journeySelection: { [quoteId: string]: string[] } = {};
    public showSorter: boolean = false;
    public notSortedQuotes: IQuotes;
    public sortOutput: ISortOutput;
    public higherPriceWidth: number = 0;

    public selectedOfferShowPermission: string = "iqba.quote.offer.selected.show";

    constructor(
        protected readonly quoteSorterService: QuoteSorterService
    ){}

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.quotes && changes.quotes.currentValue) {
            this.sort(this.sortOutput);
        }
    }

    public alternativeSelect($event: IOnAlternativeSelectOutput): void {
        this.onAlternativeSelect.emit($event);
    }

    public viewSelectedQuote(): void {
        this.onViewSelectedQuote.emit();
    }

    public keys(collection: IQuotes): string[] {
        return Object.keys(collection);
    }

    public trackByFn(index: number, quoteId: string): string {
        return quoteId;
    }

    public selectQuote(quoteAndJourneys: IOutSelectQuote): void {
        this.selectedQuoteId = quoteAndJourneys.quoteId;
        this.onSelectedQuote.emit(quoteAndJourneys);
    }
    public selectQuoteOnly(quoteAndJourneys: IOutSelectQuote): void {
        this.selectedQuoteId = quoteAndJourneys.quoteId;
        this.onSelectedQuoteOnly.emit(quoteAndJourneys);
    }

    public setJourneySelection(selection: IOutSelectQuote): void {
        this.journeySelection[selection.quoteId] = selection.selectedJourneys;
    }

    public sort(sortOutput: ISortOutput): void {
        this.sortOutput = (sortOutput || this.sortOutput);

        const quotesKeys: string[] = this.quotes ? Object.keys(this.quotes) : null;
        const selectionKeys: string[] = this.journeySelection ? Object.keys(this.journeySelection) : null;

        if (quotesKeys.length && selectionKeys.length && this.sortOutput) {
            let journeySelection: { [quoteId: string]: string[] } = this.journeySelection;
            if (quotesKeys.length < selectionKeys.length) {
                journeySelection = quotesKeys
                    .reduce((acc: { [quoteId: string]: string[] }, quoteId: string) => {
                        return {
                            ...acc,
                            [quoteId]: this.journeySelection[quoteId]
                        };
                    }, {});
            }

            this.quotes = this.quoteSorterService.sortQuotes(this.sortOutput, this.quotes, journeySelection);

        }
    }

    public uniformPriceWidth(width: number): void {
        if (width > this.higherPriceWidth) {
            this.higherPriceWidth = width;
        }
    }
}

